/* page-fulfillment-app.js: page behavior, render, interactivity
   Iteration 2 (2026-05-18): 6 manufacturing pins + Key West pickup pin.
   Haversine-over-manufacturing-facilities replaces the 2-bucket plantFor picker.
   Destination lat/lon resolved from the d3 projection.invert() of the state
   centroid (we do not have a ZIP3 centroid table in the data file).
   Iteration 3 (2026-05-18): merged Dallas + Gainesville into a single
   "North TX" pin at the midpoint (33.2013, -96.9653) since the city labels
   overlapped on the map. Added per-pin hover and click info cards reusing
   the existing .map-tooltip element. Key West pin click explicitly states
   pickup-only and surfaces the Eaton St directions link.
   Iteration 4 (2026-05-18): transit buckets floored at 2 business days and
   always carry the word "business" (no more "1 to 2 days"); North TX click
   card trimmed to the two-facility statement; per-pin labelOffsetY override
   added so Gum Springs sits ABOVE its pin (clears Arkansas state shape) and
   North TX sits 2 SVG units lower than default (clears the Texas border on
   mobile). All other pins still default to +22.
   Iteration 7 (2026-05-19): transit buckets shifted +1 across the upper end so
   no answer ever undersells the actual freight reality. New floor stays at
   "2 to 3 business days" (was a singleton "2"), mid bucket widens to "2 to 4",
   long bucket widens to "3 to 6", and the upper bucket lands at "5 to 8" for
   coast-to-coast routes. Hero subhead about "2 to 7" was removed in the
   companion snippet edit; the estimator carries the full range now. */
(async function() {
  /* ============================================================
     ZIP -> State mapping
     ============================================================ */
  const ZIP_RANGES = [
    [1000,2799,'MA'],[2800,2999,'RI'],[3000,3899,'NH'],[3900,4999,'ME'],
    [5000,5999,'VT'],[6000,6999,'CT'],[7000,8999,'NJ'],[10000,14999,'NY'],
    [15000,19699,'PA'],[19700,19999,'DE'],[20000,20599,'DC'],
    [20600,21999,'MD'],[22000,24699,'VA'],[24700,26899,'WV'],
    [27000,28999,'NC'],[29000,29999,'SC'],[30000,31999,'GA'],
    [32000,34999,'FL'],[35000,36999,'AL'],[37000,38599,'TN'],
    [38600,39799,'MS'],[40000,42799,'KY'],[43000,45999,'OH'],
    [46000,47999,'IN'],[48000,49999,'MI'],[50000,52899,'IA'],
    [53000,54999,'WI'],[55000,56799,'MN'],[57000,57799,'SD'],
    [58000,58899,'ND'],[59000,59999,'MT'],[60000,62999,'IL'],
    [63000,65899,'MO'],[66000,67999,'KS'],[68000,69399,'NE'],
    [70000,71499,'LA'],[71600,72999,'AR'],[73000,74999,'OK'],
    [75000,79999,'TX'],[80000,81699,'CO'],[82000,83199,'WY'],
    [83200,83899,'ID'],[84000,84799,'UT'],[85000,86599,'AZ'],
    [87000,88499,'NM'],[88900,89899,'NV'],[90000,96199,'CA'],
    [97000,97999,'OR'],[98000,99499,'WA'],
    [99500,99999,'AK'],[96700,96899,'HI'],[600,999,'PR'],
  ];

  const NON_CONTIG = new Set(['AK','HI','PR']);

  const STATE_NAMES = {
    AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',
    CO:'Colorado',CT:'Connecticut',DE:'Delaware',DC:'D.C.',FL:'Florida',
    GA:'Georgia',HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',
    IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',
    MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',
    MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',
    NH:'New Hampshire',NJ:'New Jersey',NM:'New Mexico',NY:'New York',
    NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',
    OR:'Oregon',PA:'Pennsylvania',PR:'Puerto Rico',RI:'Rhode Island',
    SC:'South Carolina',SD:'South Dakota',TN:'Tennessee',TX:'Texas',
    UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',WV:'West Virginia',
    WI:'Wisconsin',WY:'Wyoming'
  };

  // FIPS code -> state postal (for topojson lookup)
  const FIPS_TO_STATE = {
    '01':'AL','02':'AK','04':'AZ','05':'AR','06':'CA','08':'CO','09':'CT',
    '10':'DE','11':'DC','12':'FL','13':'GA','15':'HI','16':'ID','17':'IL',
    '18':'IN','19':'IA','20':'KS','21':'KY','22':'LA','23':'ME','24':'MD',
    '25':'MA','26':'MI','27':'MN','28':'MS','29':'MO','30':'MT','31':'NE',
    '32':'NV','33':'NH','34':'NJ','35':'NM','36':'NY','37':'NC','38':'ND',
    '39':'OH','40':'OK','41':'OR','42':'PA','44':'RI','45':'SC','46':'SD',
    '47':'TN','48':'TX','49':'UT','50':'VT','51':'VA','53':'WA','54':'WV',
    '55':'WI','56':'WY','72':'PR'
  };

  /* ============================================================
     Facilities: 5 manufacturing plants + Key West pickup yard
     Coordinates verified 2026-05-18:
       Phoenix AZ      -- city centroid (unchanged from iter 1)
       Avon OH         -- city centroid (Lorain County); GAF's silicone
                          partner facility sits in the Avon Lake adjacent
                          municipality, town-level coord is close enough
                          for nearest-plant math
       Valdosta GA     -- city centroid (Lowndes County); GAF's TPO plant
                          at Buster Bassford Industrial Park
       Gum Springs AR  -- Wikipedia CDP centroid (Clark County, 5 mi SSW
                          of Arkadelphia). Spec fallback was 34.121/-93.054
                          (Arkadelphia city), we use the actual Gum Springs
                          CDP centroid 34.0673/-93.0918 for tighter fit.
       North TX        -- midpoint of Dallas (32.7767, -96.7970) and
                          Gainesville (33.6259, -97.1336) = (33.2013,
                          -96.9653). Merged from two pins in iter-2
                          because the labels collided on the map; the
                          two plants are only 80 mi apart and GAF picks
                          the closer one by inventory, so a single pin
                          with a click-card disclosing both physical
                          plants is more honest than two pins fighting
                          for the same screen space.
       Key West FL     -- city centroid; yard at 1109 Eaton St is close.
     labelOffsetY (iter-4): per-pin vertical override for the city label.
       Default is +22 (label below pin). Gum Springs uses -12 so the
       label sits ABOVE its pin (Arkansas is short vertically and the
       label collides with the Texas border at +22). North TX uses +24
       to nudge the label 2 SVG units lower than default, clearing the
       collision with Gum Springs' new "above-pin" label on mobile.
     ============================================================ */
  const FACILITIES = [
    { id: 'phoenix',     city: 'Phoenix',     state: 'AZ', lon: -112.0740, lat: 33.4484, role: 'manufacturing',
      labelOffsetY: 26,
      info: 'GAF manufacturing facility. Phoenix, Arizona.' },
    { id: 'avon',        city: 'Avon',        state: 'OH', lon:  -82.0354, lat: 41.4517, role: 'manufacturing',
      labelOffsetY: 24,
      info: 'GAF manufacturing facility. Avon, Ohio.' },
    { id: 'valdosta',    city: 'Valdosta',    state: 'GA', lon:  -83.2785, lat: 30.8327, role: 'manufacturing',
      labelOffsetY: 25,
      info: 'GAF manufacturing facility. Valdosta, Georgia.' },
    { id: 'gumsprings',  city: 'Gum Springs', state: 'AR', lon:  -93.0918, lat: 34.0673, role: 'manufacturing',
      labelOffsetY: -19,
      info: 'GAF manufacturing facility. Gum Springs, Arkansas.' },
    { id: 'northtx',     city: 'North TX',    state: 'TX', lon:  -96.9653, lat: 33.2013, role: 'manufacturing',
      name: 'North TX',
      labelOffsetY: 26,
      info: 'Two GAF manufacturing facilities in North Texas, Dallas and Gainesville.' },
    { id: 'keywest',     city: 'Key West',    state: 'FL', lon:  -81.8043, lat: 24.5551, role: 'pickup',
      address: '1109 Eaton St, Key West FL 33040',
      info: 'This location is for pickup only. 1109 Eaton St, Key West FL 33040.',
      mapsUrl: 'https://www.google.com/maps/search/?api=1&query=1109+Eaton+St+Key+West+FL+33040' }
  ];

  // Lookup by id so the pin event handlers can resolve the facility object
  // without a linear scan on every mouseover.
  const FACILITY_BY_ID = {};
  for (const f of FACILITIES) FACILITY_BY_ID[f.id] = f;

  function haversineMiles(lon1, lat1, lon2, lat2) {
    const R = 3958.8;
    const toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  function nearestManufacturing(destLon, destLat) {
    let best = null;
    for (const f of FACILITIES) {
      if (f.role !== 'manufacturing') continue;
      const d = haversineMiles(destLon, destLat, f.lon, f.lat);
      if (!best || d < best.distance) best = Object.assign({}, f, { distance: d });
    }
    return best;
  }

  function transitFromMiles(miles) {
    if (miles < 500)  return '2 to 3 business days';
    if (miles < 1000) return '2 to 4 business days';
    if (miles < 2000) return '3 to 6 business days';
    return '5 to 8 business days';
  }

  function lookupState(zipNum) {
    for (const [lo, hi, st] of ZIP_RANGES) {
      if (zipNum >= lo && zipNum <= hi) return st;
    }
    return null;
  }

  /* ============================================================
     Load TopoJSON + render map
     ============================================================ */
  const SVG = document.getElementById('map-svg');
  const WRAP = document.getElementById('map-wrap');
  const LOADING = document.getElementById('map-loading');
  const TOOLTIP = document.getElementById('map-tooltip');

  // Iteration 1 (2026-05-17): viewBox H raised from 540 to 580 so the projection
  // has more vertical room and the Key West pin (plus its label) clears the bottom.
  const W = 960, H = 580;
  SVG.setAttribute('viewBox', `0 0 ${W} ${H}`);

  const topo = window.__INLINE_TOPO__;
  if (!topo) {
    LOADING.textContent = 'Map data not loaded.';
    return;
  }

  // Iteration 1 (2026-05-17): filter Alaska (FIPS 02), Hawaii (FIPS 15), and
  // Puerto Rico (FIPS 72) out of the rendered map. The AK/HI/PR notice text in
  // the page footer is intact; only the visual SVG insets are removed.
  const HIDDEN_FIPS = new Set(['02', '15', '72']);

  const allFeatures = topojson.feature(topo, topo.objects.states).features;
  const features = allFeatures.filter(f => !HIDDEN_FIPS.has(String(f.id).padStart(2, '0')));

  // Albers USA projection fit to viewBox using the FILTERED features so the
  // lower 48 fill the canvas instead of being squeezed up to make room for
  // the AK/HI insets.
  // Iteration 1 (2026-05-17): fitExtent with bottom padding reserved so the
  // Key West pin (which sits at the southernmost point of Florida) has room
  // to render below the geography without being clipped by the container.
  const fc = { type: 'FeatureCollection', features };
  const projection = d3.geoAlbersUsa().fitExtent([[8, 8], [W - 8, H - 40]], fc);
  const path = d3.geoPath(projection);

  // -- Render states ---------------------------------------
  const NS = 'http://www.w3.org/2000/svg';
  const gStates = document.createElementNS(NS, 'g');
  gStates.setAttribute('id', 'states');
  SVG.appendChild(gStates);

  const stateEls = {};

  features.forEach(f => {
    const fips = String(f.id).padStart(2, '0');
    const stateCode = FIPS_TO_STATE[fips];
    if (!stateCode) return;
    const d = path(f);
    if (!d) return;
    const el = document.createElementNS(NS, 'path');
    el.setAttribute('d', d);
    // Iteration 2 (2026-05-18): dropped the per-bucket region fill split.
    // All contiguous states share the uniform light fill, dest-active
    // overrides on lookup.
    el.setAttribute('class', 'state' + (NON_CONTIG.has(stateCode) ? ' non-contig' : ''));
    el.dataset.state = stateCode;
    gStates.appendChild(el);
    stateEls[stateCode] = el;
  });

  // -- Compute state centroids (in SVG coords) ------------
  // We also derive a geographic (lon/lat) centroid per state by inverting
  // the SVG centroid through the d3 projection. This gives us a destination
  // lat/lon for the Haversine pick without needing a separate ZIP3 centroid
  // table in the data file.
  const STATE_CENTROIDS = {};
  const STATE_GEO_CENTROIDS = {};
  features.forEach(f => {
    const fips = String(f.id).padStart(2, '0');
    const code = FIPS_TO_STATE[fips];
    if (!code) return;
    const c = path.centroid(f);
    if (c && !isNaN(c[0])) {
      STATE_CENTROIDS[code] = c;
      const geo = projection.invert ? projection.invert(c) : null;
      if (geo && !isNaN(geo[0])) {
        STATE_GEO_CENTROIDS[code] = { lon: geo[0], lat: geo[1] };
      }
    }
  });

  // -- Origin pins ------------------------------------------
  // Iteration 2 (2026-05-18): loop over FACILITIES so all 7 pins render
  // from a single source of truth. Pickup pins get the .kw class for
  // the blue / smaller treatment defined in CSS.
  const gPins = document.createElementNS(NS, 'g');
  gPins.setAttribute('id', 'pins');
  SVG.appendChild(gPins);

  // Cache projected facility positions so showResult can use them without
  // re-projecting on every lookup.
  const FACILITY_XY = {};

  function addPin(opts) {
    const { x, y, plant, label, kw=false } = opts;
    // Iteration 4 (2026-05-18): labelOffsetY override. Default +22 sits the
    // label below the pin (legacy behavior). Negative values place the
    // label above the pin (used by Gum Springs to avoid the AR/TX collision).
    const labelOffsetY = opts.labelOffsetY ?? 22;
    const g = document.createElementNS(NS, 'g');
    g.setAttribute('class', 'pin pulse' + (kw ? ' kw' : ''));
    g.dataset.plant = plant;
    g.setAttribute('tabindex', '0');
    g.setAttribute('role', 'button');
    g.setAttribute('aria-label', label);

    const ring = document.createElementNS(NS, 'circle');
    ring.setAttribute('class', 'pin-ring');
    ring.setAttribute('cx', x); ring.setAttribute('cy', y); ring.setAttribute('r', 7);
    g.appendChild(ring);

    const dot = document.createElementNS(NS, 'circle');
    dot.setAttribute('class', 'pin-dot');
    dot.setAttribute('cx', x); dot.setAttribute('cy', y); dot.setAttribute('r', 7);
    g.appendChild(dot);

    gPins.appendChild(g);

    // Iteration 1 (2026-05-17): pin label flipped from white-on-navy-halo to
    // dark-navy-on-white-halo so it reads cleanly over the new white hero and
    // the light state tint fills. Default label sits BELOW the pin; iter-4
    // adds the labelOffsetY override to support per-pin vertical placement.
    const text = document.createElementNS(NS, 'text');
    text.setAttribute('x', x); text.setAttribute('y', y + labelOffsetY);
    text.setAttribute('text-anchor', 'middle');
    text.setAttribute('class', 'pin-label-text');
    text.style.font = '700 10px/1 var(--font-body)';
    text.style.letterSpacing = '0.1em';
    text.style.textTransform = 'uppercase';
    text.setAttribute('fill', '#0a2540');
    text.setAttribute('paint-order', 'stroke');
    text.setAttribute('stroke', 'rgba(255,255,255,0.95)');
    text.setAttribute('stroke-width', '4');
    text.style.pointerEvents = 'none';
    text.textContent = label;
    SVG.appendChild(text);

    return g;
  }

  for (const f of FACILITIES) {
    const xy = projection([f.lon, f.lat]);
    if (!xy) continue;
    FACILITY_XY[f.id] = xy;
    const isPickup = f.role === 'pickup';
    // Iteration 3 (2026-05-18): the merged North TX entry uses its own short
    // label (no "City · ST" form) since it represents two cities. Every other
    // pin keeps the city + state label.
    const pinLabel = f.id === 'northtx' ? 'North TX' : (f.city + ' · ' + f.state);
    addPin({
      x: xy[0], y: xy[1],
      plant: f.id,
      label: pinLabel,
      kw: isPickup,
      labelOffsetY: f.labelOffsetY
    });
  }

  // -- Ship line + destination pin layer -------------------
  const shipLine = document.createElementNS(NS, 'path');
  shipLine.setAttribute('id', 'ship-line');
  shipLine.setAttribute('class', 'ship-line');
  shipLine.setAttribute('d', '');
  SVG.appendChild(shipLine);

  // Iteration 1 (2026-05-17): destination pin recolored from white-on-blue to
  // cyan-on-white so it reads on the new white background.
  const destPin = document.createElementNS(NS, 'g');
  destPin.setAttribute('id', 'dest-pin');
  destPin.setAttribute('class', 'dest-pin');
  destPin.innerHTML = `
    <circle class="pin-ring" cx="0" cy="0" r="6" stroke="#2789ab" stroke-width="2" fill="none"/>
    <circle cx="0" cy="0" r="6" fill="#fff" stroke="#2789ab" stroke-width="2"/>
    <circle cx="0" cy="0" r="2.5" fill="#2789ab"/>
  `;
  SVG.appendChild(destPin);

  LOADING.style.display = 'none';

  /* ============================================================
     Tooltip core: shared between state-hover, pin-hover, and pin-click.
     Iteration 3 (2026-05-18): the single .map-tooltip element is reused
     across three modes. A click on a pin marks the tooltip "sticky" so
     it survives mouseout. A click outside any pin (or Escape) clears it.
     When the tooltip is sticky we suppress the state-hover content path
     so the user's chosen pin card wins.
     ============================================================ */
  let tooltipSticky = false;
  let tooltipMode = null;    // 'state' | 'pin' | null
  let tooltipPinId = null;   // facility id when mode === 'pin'

  function positionTooltip(el) {
    const wrapRect = WRAP.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    const x = (elRect.left + elRect.width / 2) - wrapRect.left;
    const y = (elRect.top + elRect.height / 2) - wrapRect.top;
    TOOLTIP.style.left = x + 'px';
    TOOLTIP.style.top  = y + 'px';
  }

  function showTooltip(el, html, evt) {
    TOOLTIP.innerHTML = html;
    positionTooltip(el);
    TOOLTIP.classList.add('show');
  }
  function hideTooltip() {
    if (tooltipSticky) return; // sticky cards survive hover-out
    TOOLTIP.classList.remove('show');
    TOOLTIP.classList.remove('pin-info', 'sticky');
    tooltipMode = null;
    tooltipPinId = null;
  }
  function forceHideTooltip() {
    tooltipSticky = false;
    TOOLTIP.classList.remove('show', 'pin-info', 'sticky');
    tooltipMode = null;
    tooltipPinId = null;
  }

  // showPinInfo: render and place the .map-tooltip in pin-info mode at the
  // pin's screen position. `sticky=true` is the click path; the card remains
  // until the user clicks elsewhere or presses Escape.
  function showPinInfo(facility, anchorEl, sticky) {
    if (!facility || !anchorEl) return;
    const label = facility.id === 'northtx'
      ? 'North TX'
      : (facility.city + ', ' + facility.state);
    const info = facility.info || '';
    const directions = (facility.role === 'pickup' && facility.mapsUrl)
      ? `<a class="tt-link" href="${facility.mapsUrl}" target="_blank" rel="noopener">Get directions</a>`
      : '';
    const close = sticky
      ? `<button type="button" class="tt-close" aria-label="Close">
           <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
             <path d="M3 3 L13 13 M13 3 L3 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/>
           </svg>
         </button>`
      : '';
    TOOLTIP.innerHTML = `
      ${close}
      <div class="tt-state">${label}</div>
      <div class="tt-info">${info}</div>
      ${directions}
    `;
    positionTooltip(anchorEl);
    TOOLTIP.classList.add('show', 'pin-info');
    TOOLTIP.classList.toggle('sticky', !!sticky);
    tooltipMode = 'pin';
    tooltipPinId = facility.id;
    tooltipSticky = !!sticky;
  }

  /* ============================================================
     State hover: highlight + tooltip
     ============================================================ */
  gStates.addEventListener('mouseover', (e) => {
    const t = e.target.closest('.state'); if (!t) return;
    t.classList.add('hovering');
    // Iteration 3: a sticky pin card wins over state-hover content.
    if (tooltipSticky && tooltipMode === 'pin') return;
    const code = t.dataset.state;
    if (!code) return;
    // Iteration 2 (2026-05-18): tooltip shows the nearest manufacturing
    // plant for this state's geographic centroid + the transit bucket.
    let html;
    if (NON_CONTIG.has(code)) {
      html = `<div class="tt-state">${STATE_NAMES[code]}</div>
              <div class="tt-plant"><span class="ll">●</span> Call us, non-contiguous</div>`;
    } else {
      const geo = STATE_GEO_CENTROIDS[code];
      const picked = geo ? nearestManufacturing(geo.lon, geo.lat) : null;
      if (picked) {
        // Iteration 3: merged North TX shows its own clean label instead of
        // "Ships from Midpoint TX, TX".
        const fromLabel = picked.id === 'northtx'
          ? 'Ships from North TX'
          : `Ships from ${picked.city}, ${picked.state}`;
        html = `<div class="tt-state">${STATE_NAMES[code]}</div>
                <div class="tt-plant"><span class="ll">●</span> ${fromLabel}</div>
                <div class="tt-transit">Transit · ${transitFromMiles(picked.distance)}</div>`;
      } else {
        html = `<div class="tt-state">${STATE_NAMES[code]}</div>
                <div class="tt-plant"><span class="ll">●</span> Call us for a quote</div>`;
      }
    }
    TOOLTIP.classList.remove('pin-info', 'sticky');
    showTooltip(t, html, e);
    tooltipMode = 'state';
    tooltipPinId = null;
  });
  gStates.addEventListener('mouseout', (e) => {
    const t = e.target.closest('.state'); if (!t) return;
    t.classList.remove('hovering');
    hideTooltip();
  });
  // Click a state to run the same routing animation as the ZIP estimator
  gStates.addEventListener('click', (e) => {
    const t = e.target.closest('.state'); if (!t) return;
    // If a sticky pin card is open, a click on the underlying state should
    // close the card instead of triggering a state-route animation. The
    // document-level handler below will close the card for us; bail here.
    if (tooltipSticky && tooltipMode === 'pin') return;
    const code = t.dataset.state; if (!code) return;
    forceHideTooltip();
    // Pick a representative ZIP for the state by scanning its range
    const range = ZIP_RANGES.find(r => r[2] === code);
    const repZip = range ? String(range[0]).padStart(5, '0') : '';
    showResult(repZip, code, { fromState: true });
  });

  /* ============================================================
     Pin hover + click: per-pin info card
     Iteration 3 (2026-05-18): every pin shows a 1-2 line factual info card
     reusing the existing .map-tooltip element. Hover shows the card briefly;
     click pins it (sticky) until the user clicks outside or hits Escape.
     ============================================================ */
  gPins.addEventListener('mouseover', (e) => {
    const pin = e.target.closest('.pin'); if (!pin) return;
    if (tooltipSticky) return; // sticky card wins
    const id = pin.dataset.plant;
    const facility = FACILITY_BY_ID[id];
    if (!facility) return;
    showPinInfo(facility, pin, false);
  });
  gPins.addEventListener('mouseout', (e) => {
    const pin = e.target.closest('.pin'); if (!pin) return;
    // Hide only if we are not moving to another pin and not sticky.
    const related = e.relatedTarget && e.relatedTarget.closest
      ? e.relatedTarget.closest('.pin')
      : null;
    if (related) return;
    hideTooltip();
  });
  gPins.addEventListener('click', (e) => {
    const pin = e.target.closest('.pin'); if (!pin) return;
    e.stopPropagation();
    const id = pin.dataset.plant;
    const facility = FACILITY_BY_ID[id];
    if (!facility) return;
    // Second click on the SAME sticky pin closes the card.
    if (tooltipSticky && tooltipMode === 'pin' && tooltipPinId === id) {
      forceHideTooltip();
      return;
    }
    showPinInfo(facility, pin, true);
  });

  // Keyboard activation: Enter or Space on a focused pin opens the card.
  gPins.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const pin = e.target.closest('.pin'); if (!pin) return;
    e.preventDefault();
    const id = pin.dataset.plant;
    const facility = FACILITY_BY_ID[id];
    if (!facility) return;
    if (tooltipSticky && tooltipMode === 'pin' && tooltipPinId === id) {
      forceHideTooltip();
      return;
    }
    showPinInfo(facility, pin, true);
  });

  // Close sticky tooltip on any click outside a pin OR on the close button.
  document.addEventListener('click', (e) => {
    if (!tooltipSticky) return;
    if (e.target.closest && e.target.closest('.tt-close')) {
      forceHideTooltip();
      return;
    }
    // Allow clicking inside the tooltip (e.g. the Directions link) without
    // dismissing it.
    if (e.target.closest && e.target.closest('.map-tooltip')) return;
    if (e.target.closest && e.target.closest('.pin')) return;
    forceHideTooltip();
  });

  // Escape closes any sticky tooltip.
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && tooltipSticky) {
      forceHideTooltip();
    }
  });

  /* ============================================================
     ZIP estimator
     ============================================================ */
  const form  = document.getElementById('zip-form');
  const input = document.getElementById('zip-input');
  const result= document.getElementById('est-result');
  const rZip  = document.getElementById('r-zip');
  const rSt   = document.getElementById('r-state');
  const rMain = document.getElementById('r-main');
  const rMeta = document.getElementById('r-meta');
  const rCaveat = document.getElementById('r-caveat');

  input.addEventListener('input', () => {
    input.value = input.value.replace(/[^0-9]/g, '').slice(0, 5);
  });

  function clearMap() {
    shipLine.setAttribute('d', '');
    shipLine.style.strokeDashoffset = '';
    shipLine.style.transition = '';
    destPin.classList.remove('show');
    document.querySelectorAll('.state.dest-active').forEach(s => s.classList.remove('dest-active'));
  }

  function drawShipLine(ox, oy, dx, dy) {
    const mx = (ox + dx) / 2;
    const my = Math.min(oy, dy) - Math.abs(dx - ox) * 0.18 - 30;
    const d = `M ${ox} ${oy} Q ${mx} ${my} ${dx} ${dy}`;
    shipLine.setAttribute('d', d);
    requestAnimationFrame(() => {
      const len = shipLine.getTotalLength();
      shipLine.style.strokeDasharray = len;
      shipLine.style.strokeDashoffset = len;
      shipLine.style.transition = 'none';
      void shipLine.getBBox();
      requestAnimationFrame(() => {
        shipLine.style.transition = 'stroke-dashoffset 1100ms cubic-bezier(.6,.2,.3,1)';
        shipLine.style.strokeDashoffset = '0';
      });
    });
  }

  function placeDestPin(x, y) {
    destPin.querySelectorAll('circle').forEach(c => {
      c.setAttribute('cx', x); c.setAttribute('cy', y);
    });
    destPin.classList.remove('show');
    void destPin.getBoundingClientRect();
    setTimeout(() => destPin.classList.add('show'), 50);
  }

  // Iteration 2 (2026-05-18): showResult signature dropped the legacy "plant"
  // arg. Plant is now picked inside the function via Haversine.
  function showResult(zip, state, opts = {}) {
    const fromState = !!opts.fromState;
    result.classList.remove('show', 'bad');
    void result.offsetWidth;

    if (!state) {
      rZip.textContent = zip;
      rSt.textContent = 'Not recognized';
      rMain.innerHTML = "Sorry, that ZIP isn't in our database.";
      rMeta.innerHTML = '';
      if (rCaveat) rCaveat.textContent = '';
      result.classList.add('show', 'bad');
      return;
    }
    if (NON_CONTIG.has(state)) {
      rZip.textContent = fromState ? STATE_NAMES[state] : zip;
      rSt.textContent  = fromState ? 'Non-contiguous' : (STATE_NAMES[state] || state);
      rMain.innerHTML  = `<b>${STATE_NAMES[state]}</b>. Call us for freight or non-contiguous orders.`;
      rMeta.innerHTML  = '';
      if (rCaveat) rCaveat.textContent = '';
      result.classList.add('show', 'bad');
      return;
    }

    // Pick nearest manufacturing plant via Haversine over geographic state
    // centroid (derived from projection.invert). If projection.invert is not
    // available, fall back to a bad-state response.
    const geo = STATE_GEO_CENTROIDS[state];
    const picked = geo ? nearestManufacturing(geo.lon, geo.lat) : null;

    if (!picked) {
      rZip.textContent = zip;
      rSt.textContent  = STATE_NAMES[state] || state;
      rMain.innerHTML  = "Sorry, that ZIP isn't in our database.";
      rMeta.innerHTML  = '';
      if (rCaveat) rCaveat.textContent = '';
      result.classList.add('show', 'bad');
      return;
    }

    // Iteration 3 (2026-05-18): merged North TX entry overrides the
    // default "City, ST" origin name with its own `name` field.
    const originName = picked.name || (picked.city + ', ' + picked.state);
    const origin = {
      name: originName,
      xy: FACILITY_XY[picked.id] || projection([picked.lon, picked.lat]),
      transit: transitFromMiles(picked.distance),
      miles: picked.distance
    };

    if (fromState) {
      rZip.textContent = STATE_NAMES[state] || state;
      rSt.textContent  = 'Tap a state · representative route';
    } else {
      rZip.textContent = zip;
      rSt.textContent  = STATE_NAMES[state] || state;
    }
    rMain.innerHTML  = `Ships from <span class="from">${origin.name}</span><span class="arrow">→</span><b>${STATE_NAMES[state]}</b>`;
    rMeta.innerHTML  = `<span>Transit · <b>${origin.transit}</b></span><span>Plant · <b>${origin.name}</b></span>`;
    if (rCaveat) {
      rCaveat.textContent = 'Origin is assigned per order based on inventory. Closest plant shown as a typical estimate.';
    }
    result.classList.add('show');

    const dest = STATE_CENTROIDS[state];
    clearMap();
    if (stateEls[state]) stateEls[state].classList.add('dest-active');

    setTimeout(() => {
      if (origin.xy && dest) {
        drawShipLine(origin.xy[0], origin.xy[1], dest[0], dest[1]);
        placeDestPin(dest[0], dest[1]);
      }
    }, 100);
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const zip = input.value.trim();
    if (zip.length !== 5) { input.focus(); return; }
    const zipNum = parseInt(zip, 10);
    const state = lookupState(zipNum);
    showResult(zip, state);
  });
})();
